import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Store } from "lucide-react";

interface BusinessRow {
  user_id: string;
  display_name: string;
  cashtag: string;
  store_name: string | null;
  avatar_url: string | null;
  category?: string | null;
}

const BusinessDirectory = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BusinessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState<string>("all");

  useEffect(() => {
    (async () => {
      const { data: profs } = await supabase
        .from("public_profiles")
        .select("user_id, display_name, cashtag, store_name, avatar_url")
        .eq("is_merchant", true)
        .limit(500);

      const list = (profs as BusinessRow[] | null) ?? [];
      const ids = list.map((m) => m.user_id);

      const catMap: Record<string, string> = {};
      if (ids.length) {
        const { data: apps } = await supabase
          .from("merchant_applications")
          .select("user_id, category, status")
          .in("user_id", ids)
          .eq("status", "approved");
        (apps ?? []).forEach((a: any) => {
          if (a.category) catMap[a.user_id] = a.category;
        });
      }
      setRows(list.map((m) => ({ ...m, category: catMap[m.user_id] ?? null })));
      setLoading(false);
    })();
  }, []);

  const cats = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.category && s.add(r.category));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((m) => {
      if (activeCat !== "all" && (m.category ?? "") !== activeCat) return false;
      if (!needle) return true;
      return (
        m.display_name?.toLowerCase().includes(needle) ||
        m.cashtag?.toLowerCase().includes(needle) ||
        (m.store_name ?? "").toLowerCase().includes(needle) ||
        (m.category ?? "").toLowerCase().includes(needle)
      );
    });
  }, [rows, q, activeCat]);

  return (
    <MobileShell>
      <Helmet>
        <title>Business Directory — Cash Isle</title>
        <meta name="description" content="Discover local Bahamian businesses on Cash Isle. Browse shops, services, and creators accepting Cash Isle payments." />
        <link rel="canonical" href="https://cash-isle-hustle-hub.lovable.app/businesses" />
        <meta property="og:title" content="Business Directory — Cash Isle" />
        <meta property="og:description" content="Discover local Bahamian businesses on Cash Isle." />
        <meta property="og:url" content="https://cash-isle-hustle-hub.lovable.app/businesses" />
      </Helmet>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Business Directory</h1>
        <p className="text-center text-xs opacity-80 mt-1">Local businesses on Cash Isle</p>
      </BrandHeader>

      <div className="px-5 -mt-5">
        <div className="bg-background rounded-2xl shadow-card p-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search businesses…"
              className="pl-9 h-11 rounded-xl bg-secondary/60 border-transparent"
            />
          </div>
          {cats.length > 0 && (
            <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
              <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")}>All</CatChip>
              {cats.map((c) => (
                <CatChip key={c} active={activeCat === c} onClick={() => setActiveCat(c)}>{c}</CatChip>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-5 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Store className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No businesses found{q ? ` for "${q}"` : ""}.</p>
          </div>
        ) : (
          filtered.map((b) => (
            <Link
              key={b.user_id}
              to={`/u/${b.cashtag}`}
              className="flex items-center gap-3 p-3 bg-background rounded-2xl shadow-card hover:bg-secondary/30 transition"
            >
              <div className="h-12 w-12 rounded-xl bg-secondary grid place-items-center overflow-hidden flex-shrink-0">
                {b.avatar_url ? (
                  <img src={b.avatar_url} alt={b.store_name ?? b.display_name} className="h-full w-full object-cover" />
                ) : (
                  <Store className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{b.store_name ?? b.display_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  ${b.cashtag}{b.category ? ` · ${b.category}` : ""}
                </p>
              </div>
            </Link>
          ))
        )}
      </div>
    </MobileShell>
  );
};

const CatChip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition ${
      active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary/60 border-transparent text-foreground"
    }`}
  >
    {children}
  </button>
);

export default BusinessDirectory;
